// Class Schedule Block Component
// Display monthly class schedule calendar
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../auth/AuthContext';

const ClassScheduleBlock = ({ content, padding, backgroundColor, gridWidth, gridHeight, onUpdate, onDelete }) => {
  const { user } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [months, setMonths] = useState(content?.months || 3);
  const [includeCurrentMonth, setIncludeCurrentMonth] = useState(content?.includeCurrentMonth || false);
  const [editPadding, setEditPadding] = useState(padding || 'medium');
  const [editBackgroundColor, setEditBackgroundColor] = useState(backgroundColor || '');
  const [editGridWidth, setEditGridWidth] = useState(gridWidth || 12);
  const [editGridHeight, setEditGridHeight] = useState(gridHeight || 4);
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [categoryFilter, setCategoryFilter] = useState(content?.categoryFilter || '');
  const [skillLevelFilter, setSkillLevelFilter] = useState(content?.skillLevelFilter || '');
  const [instructorFilter, setInstructorFilter] = useState(content?.instructorFilter || '');
  const [tagsFilter, setTagsFilter] = useState(content?.tagsFilter || []);
  const [tagsInput, setTagsInput] = useState((content?.tagsFilter || []).join(', '));
  const [priceButtonColor, setPriceButtonColor] = useState(content?.priceButtonColor || '#e5e7eb');
  const [cardBackgroundColor, setCardBackgroundColor] = useState(content?.cardBackgroundColor || '#ffffff');
  const [title, setTitle] = useState(content?.title || 'Upcoming Classes');
  const [titleFontFamily, setTitleFontFamily] = useState(content?.titleFontFamily || content?.fontFamily || 'system');
  const [contentFontFamily, setContentFontFamily] = useState(content?.contentFontFamily || 'system');

  // Helper to determine if a color is light or dark
  const isLightColor = (color) => {
    if (!color || color === 'transparent') return true;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 186; // Higher threshold for better contrast
  };

  const getTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-900' : 'text-white';
  };

  const getSubTextColorClass = (bgColor) => {
    return isLightColor(bgColor) ? 'text-gray-700' : 'text-gray-100';
  };

  // Font options
  const fonts = [
    { value: 'system', label: 'System Default' },
    { value: 'Coustard', label: 'Coustard' },
    { value: 'Alfa Slab One', label: 'Alfa Slab One' },
    { value: 'Ovo', label: 'Ovo' },
    { value: 'Fraunces', label: 'Fraunces' },
    { value: 'Lancelot', label: 'Lancelot' },
    { value: 'Arvo', label: 'Arvo' },
    { value: 'Josefin Slab', label: 'Josefin Slab' },
    { value: 'Outfit', label: 'Outfit' },
    { value: 'Cal Sans', label: 'Cal Sans' },
    { value: 'Tilt Warp', label: 'Tilt Warp' },
    { value: 'McLaren', label: 'McLaren' },
    { value: 'Mate', label: 'Mate' },
    { value: 'Buda', label: 'Buda' },
    { value: 'Bellefair', label: 'Bellefair' },
    { value: 'Questrial', label: 'Questrial' },
    { value: 'DM Mono', label: 'DM Mono' },
    { value: 'Quicksand', label: 'Quicksand' },
    { value: 'Nunito Sans', label: 'Nunito Sans' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Manrope', label: 'Manrope' },
    { value: 'Jost', label: 'Jost' }
  ];

  const getFontFamily = (fontName) => {
    if (!fontName || fontName === 'system') {
      return 'ui-sans-serif, system-ui, sans-serif';
    }
    return `"${fontName}", sans-serif`;
  };

  // Options for dropdowns
  const [categories, setCategories] = useState([]);
  const [skillLevels, setSkillLevels] = useState([]);
  const [instructors, setInstructors] = useState([]);

  useEffect(() => {
    fetchData();
  }, [content?.months, content?.includeCurrentMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        console.error('No user ID available for ClassScheduleBlock');
        setLoading(false);
        return;
      }
      // Fetch sessions from public endpoint (includes class data)
      const includeCurrentMonthParam = content?.includeCurrentMonth ? 'true' : 'false';
      const sessionsResponse = await api.request(`/class-sessions/public?months=${content?.months || 3}&includeCurrentMonth=${includeCurrentMonthParam}&userId=${user.id}`);

      const sessionsData = sessionsResponse.sessions || [];

      // Extract class data from sessions
      const classMap = {};
      sessionsData.forEach(session => {
        if (session.class) {
          classMap[session.class.id] = session.class;
        }
      });
      const classesData = Object.values(classMap);

      setClasses(classesData);
      setSessions(sessionsData);

      // Extract unique categories, skill levels, and instructors from classes
      const uniqueCategories = [...new Set(classesData.map(c => c.category).filter(Boolean))];
      const uniqueSkillLevels = [...new Set(classesData.map(c => c.skill_level).filter(Boolean))];
      const uniqueInstructors = [...new Set(classesData.map(c => c.instructor).filter(Boolean))];

      setCategories(uniqueCategories);
      setSkillLevels(uniqueSkillLevels);
      setInstructors(uniqueInstructors);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    // Parse tags from input
    const parsedTags = tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    onUpdate({
      content: {
        months,
        includeCurrentMonth,
        categoryFilter,
        skillLevelFilter,
        instructorFilter,
        tagsFilter: parsedTags,
        priceButtonColor,
        cardBackgroundColor,
        title,
        titleFontFamily,
        contentFontFamily
      },
      padding: editPadding,
      backgroundColor: editBackgroundColor || null,
      gridWidth: editGridWidth,
      gridHeight: editGridHeight
    });
    setShowEditModal(false);
  };

  const handleCancel = () => {
    setMonths(content?.months || 3);
    setIncludeCurrentMonth(content?.includeCurrentMonth || false);
    setCategoryFilter(content?.categoryFilter || '');
    setSkillLevelFilter(content?.skillLevelFilter || '');
    setInstructorFilter(content?.instructorFilter || '');
    setTagsFilter(content?.tagsFilter || []);
    setTagsInput((content?.tagsFilter || []).join(', '));
    setPriceButtonColor(content?.priceButtonColor || '#e5e7eb');
    setCardBackgroundColor(content?.cardBackgroundColor || '#ffffff');
    setTitle(content?.title || 'Upcoming Classes');
    setTitleFontFamily(content?.titleFontFamily || content?.fontFamily || 'system');
    setContentFontFamily(content?.contentFontFamily || 'system');
    setEditPadding(padding || 'medium');
    setEditBackgroundColor(backgroundColor || '');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 4);
    setShowEditModal(false);
  };

  const openEditModal = () => {
    setMonths(content?.months || 3);
    setIncludeCurrentMonth(content?.includeCurrentMonth || false);
    setCategoryFilter(content?.categoryFilter || '');
    setSkillLevelFilter(content?.skillLevelFilter || '');
    setInstructorFilter(content?.instructorFilter || '');
    setTagsFilter(content?.tagsFilter || []);
    setTagsInput((content?.tagsFilter || []).join(', '));
    setPriceButtonColor(content?.priceButtonColor || '#e5e7eb');
    setCardBackgroundColor(content?.cardBackgroundColor || '#ffffff');
    setTitle(content?.title || 'Upcoming Classes');
    setTitleFontFamily(content?.titleFontFamily || content?.fontFamily || 'system');
    setContentFontFamily(content?.contentFontFamily || 'system');
    setEditPadding(padding || 'medium');
    setEditBackgroundColor(backgroundColor || '');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 4);
    setShowEditModal(true);
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'none': return 'p-0';
      case 'small': return 'p-2';
      case 'large': return 'p-6';
      default: return 'p-4';
    }
  };

  // Get upcoming sessions with filters applied
  const getUpcomingSessions = () => {
    return sessions.filter(session => {
      // Get the parent class for filtering (already included in session)
      const cls = session.class;
      if (!cls) return false;

      // Category filter
      if (content?.categoryFilter && cls.category !== content.categoryFilter) return false;

      // Skill level filter
      if (content?.skillLevelFilter && cls.skill_level !== content.skillLevelFilter) return false;

      // Instructor filter
      if (content?.instructorFilter && cls.instructor !== content.instructorFilter) return false;

      // Tags filter
      if (content?.tagsFilter && content.tagsFilter.length > 0) {
        const classTags = cls.tags || [];
        const hasMatchingTag = content.tagsFilter.some(tag =>
          classTags.some(classTag =>
            classTag.toLowerCase().includes(tag.toLowerCase())
          )
        );
        if (!hasMatchingTag) return false;
      }

      return true;
    });
  };

  // Render edit modal
  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
            Edit Class Schedule
          </h3>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Section Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Upcoming Classes"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
            />
          </div>

          {/* Title Font Family */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Title Font
            </label>
            <select
              value={titleFontFamily}
              onChange={(e) => setTitleFontFamily(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              style={{ fontFamily: getFontFamily(titleFontFamily) }}
            >
              {fonts.map(font => (
                <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                  {font.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Font for the section title
            </p>
          </div>

          {/* Content Font Family */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Content Font
            </label>
            <select
              value={contentFontFamily}
              onChange={(e) => setContentFontFamily(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              style={{ fontFamily: getFontFamily(contentFontFamily) }}
            >
              {fonts.map(font => (
                <option key={font.value} value={font.value} style={{ fontFamily: getFontFamily(font.value) }}>
                  {font.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Font for class cards and month headers
            </p>
          </div>

          {/* Months to display */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-2">
              Number of Months to Display
            </label>
            <select
              value={months}
              onChange={(e) => setMonths(parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
            >
              <option value={1}>1 Month</option>
              <option value={2}>2 Months</option>
              <option value={3}>3 Months</option>
              <option value={4}>4 Months</option>
              <option value={5}>5 Months</option>
              <option value={6}>6 Months</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Shows classes starting within this time period
            </p>
          </div>

          {/* Include Current Month */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeCurrentMonth}
                onChange={(e) => setIncludeCurrentMonth(e.target.checked)}
                className="w-4 h-4 text-[#7A99D8] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7A99D8] focus:ring-2"
              />
              <span className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100">
                Include Current Month
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              If unchecked, only future months will be shown
            </p>
          </div>

          {/* Filter Options */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
              Filter Classes
            </h4>

            {/* Category Filter */}
            <div className="mb-3">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Category
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Skill Level Filter */}
            <div className="mb-3">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Skill Level
              </label>
              <select
                value={skillLevelFilter}
                onChange={(e) => setSkillLevelFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              >
                <option value="">All Skill Levels</option>
                {skillLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            {/* Instructor Filter */}
            <div className="mb-3">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Instructor
              </label>
              <select
                value={instructorFilter}
                onChange={(e) => setInstructorFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              >
                <option value="">All Instructors</option>
                {instructors.map(instructor => (
                  <option key={instructor} value={instructor}>{instructor}</option>
                ))}
              </select>
            </div>

            {/* Tags Filter */}
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="e.g., kids, adults, beginner"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">
                Filter classes that have any of these tags
              </p>
            </div>
          </div>

          {/* Appearance Settings */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
              Appearance
            </h4>

            {/* Padding */}
            <div className="mb-3">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Padding
              </label>
              <select
                value={editPadding}
                onChange={(e) => setEditPadding(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-[#3C3C3C] dark:text-gray-100"
              >
                <option value="none">None</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>

            {/* Background Color */}
            <div className="mb-3">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Container Background
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={editBackgroundColor || '#ffffff'}
                  onChange={(e) => setEditBackgroundColor(e.target.value)}
                  className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                />
                <button
                  onClick={() => setEditBackgroundColor('')}
                  className={`text-xs whitespace-nowrap ${editBackgroundColor ? 'text-gray-500 hover:text-red-500' : 'text-gray-300'}`}
                >
                  {editBackgroundColor ? 'Clear' : 'No Color'}
                </button>
              </div>
            </div>

            {/* Card Background Color */}
            <div className="mb-3">
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Card Background
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={cardBackgroundColor || '#ffffff'}
                  onChange={(e) => setCardBackgroundColor(e.target.value)}
                  className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                />
                <button
                  onClick={() => setCardBackgroundColor('#ffffff')}
                  className="text-xs text-gray-500 hover:text-red-500 whitespace-nowrap"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Price Button Color */}
            <div>
              <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                Price Button Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={priceButtonColor || '#e5e7eb'}
                  onChange={(e) => setPriceButtonColor(e.target.value)}
                  className="w-full h-10 px-1 py-1 border border-gray-300 dark:border-gray-600 rounded-lg"
                />
                <button
                  onClick={() => setPriceButtonColor('#e5e7eb')}
                  className="text-xs text-gray-500 hover:text-red-500 whitespace-nowrap"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
          >
            Update Schedule
          </button>
        </div>
      </div>
    </div>
  );

  const upcomingSessions = getUpcomingSessions();

  // Group sessions by class and get date ranges
  const getClassesWithDateRanges = () => {
    const classMap = {};

    upcomingSessions.forEach(session => {
      const classId = session.class?.id;
      if (!classId) return;

      if (!classMap[classId]) {
        classMap[classId] = {
          class: session.class,
          sessions: [],
          // Use actual first/last session dates for the full class range
          startDate: session.class?.first_session_date || session.session_date,
          endDate: session.class?.last_session_date || session.session_date,
          startTime: session.start_time,
          currentEnrollment: session.class?.current_enrollment || 0,
          maxCapacity: session.class?.max_capacity || 0
        };
      }

      classMap[classId].sessions.push(session);
    });

    // Sort by start date
    return Object.values(classMap).sort((a, b) =>
      new Date(a.startDate) - new Date(b.startDate)
    );
  };

  const classesWithRanges = getClassesWithDateRanges();

  const formatDate = (dateStr) => {
    // Parse date string as local time to avoid timezone issues
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const hour12 = hours % 12 || 12;
    return minutes === 0 ? `${hour12}${period}` : `${hour12}:${minutes.toString().padStart(2, '0')}${period}`;
  };

  // Helper to parse date string as local time
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Group classes by month
  const getClassesByMonth = () => {
    const monthMap = {};

    classesWithRanges.forEach(item => {
      const date = parseLocalDate(item.startDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthLabel = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      if (!monthMap[monthKey]) {
        monthMap[monthKey] = {
          label: monthLabel,
          classes: []
        };
      }
      monthMap[monthKey].classes.push(item);
    });

    // Sort by month key and return as array
    return Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, value]) => value);
  };

  const classesByMonth = getClassesByMonth();

  return (
    <>
      {/* Hidden edit trigger for PageBuilder */}
      <button
        data-edit-trigger
        onClick={openEditModal}
        className="hidden"
        aria-hidden="true"
      />
      <div
        className={`${getPaddingClass()} relative rounded-lg`}
        style={{
          backgroundColor
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7A99D8]"></div>
          </div>
        ) : classesWithRanges.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Calendar className="w-12 h-12 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No upcoming classes</p>
          </div>
        ) : (
          <>
            <h3
              className={`font-semibold text-xl mb-4 ${getTextColorClass(backgroundColor)}`}
              style={{ fontFamily: getFontFamily(content?.titleFontFamily || content?.fontFamily) }}
            >
              {content?.title || 'Upcoming Classes'}
            </h3>
            <div className="space-y-8" style={{ fontFamily: getFontFamily(content?.contentFontFamily) }}>
              {classesByMonth.map((monthGroup) => (
                <div key={monthGroup.label} className="mb-6">
                  <h4 className={`font-medium text-sm mb-2 uppercase tracking-wide flex items-center gap-2 ${getSubTextColorClass(backgroundColor)}`}>
                    <Calendar className="w-4 h-4" />
                    {monthGroup.label}
                  </h4>
                  <div className="space-y-3">
                    {monthGroup.classes.map((item) => {
                      const availableSlots = item.maxCapacity - item.currentEnrollment;
                      const isFull = availableSlots <= 0;
                      const isLowAvailability = availableSlots > 0 && availableSlots < 3;
                      const cardBg = content?.cardBackgroundColor || '#ffffff';

                      return (
                        <div
                          key={item.class.id}
                          className="rounded-lg shadow-md border border-gray-200 dark:border-gray-600 relative"
                          style={{ padding: '15px', backgroundColor: cardBg }}
                        >
                          <h4 className={`font-medium ${getTextColorClass(cardBg)}`}>
                            {item.class.title}
                          </h4>
                          <p className={`text-sm ${getSubTextColorClass(cardBg)}`}>
                            {item.class.day_of_week ? `${item.class.day_of_week}s, ` : ''}{formatDate(item.startDate)} - {formatDate(item.endDate)}{item.startTime ? `, ${formatTime(item.startTime)}` : ''}
                          </p>
                          {isFull ? (
                            <span
                              className={`inline-block px-4 py-1 rounded-full text-sm font-semibold mt-2 ${getTextColorClass(content?.priceButtonColor || '#e5e7eb')}`}
                              style={{ backgroundColor: content?.priceButtonColor || '#e5e7eb' }}
                            >
                              Join Waitlist
                            </span>
                          ) : item.class.price && (
                            <span
                              className={`inline-block px-4 py-1 rounded-full text-sm font-semibold mt-2 ${getTextColorClass(content?.priceButtonColor || '#e5e7eb')}`}
                              style={{ backgroundColor: content?.priceButtonColor || '#e5e7eb' }}
                            >
                              ${parseFloat(item.class.price).toFixed(2)}
                            </span>
                          )}
                          {item.maxCapacity > 0 && (
                            <span className={`absolute bottom-2 right-2 text-xs ${isFull ? 'text-red-500 font-medium' : isLowAvailability ? 'text-pink-500 font-medium' : getSubTextColorClass(cardBg)}`}>
                              {isFull ? 'Class Full' : `${availableSlots} spots left`}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {showEditModal && createPortal(renderEditModal(), document.body)}
    </>
  );
};

export default ClassScheduleBlock;
